import { useState } from 'react';
import { Box, ButtonBase, ClickAwayListener, makeStyles, Zoom, Tooltip } from '@material-ui/core';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { LANGUAGE_ZH_HK } from '../../comm/config';
import { CmsAlertStandardIcon, CmsAlertWideIcon } from '../Icon';
import { isNotEmptyArray } from '../../utils/newBedStatusUtil';

export const CMS_ALERT_ICON_VARIANT = {
  STANDARD: 'standard',
  WIDE: 'wide',
};

const useStyles = makeStyles(() => ({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.313rem',
    overflow: 'visible',
  },
  standardIcon: {
    display: 'block',
    flexShrink: 0,
    width: '1.85rem',
    height: '1.6rem',
  },
  wideIcon: {
    display: 'block',
    flexShrink: 0,
    width: '4rem',
    height: '2rem',
  },
  dialog: {
    '& .MuiDialog-paper': {
      width: '45.438rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100vh - 4rem)',
      margin: '2rem',
      padding: '1.875rem',
      borderRadius: '1rem',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
      overflowY: 'auto',
    },
  },
  alertList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  alertItem: {
    position: 'relative',
    minHeight: '6.375rem',
    padding: '1rem 1.625rem',
    borderRadius: '0.625rem',
    backgroundColor: '#eff3ff',
    overflow: 'hidden',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.857rem',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: '0.563rem',
      borderRadius: '0.625rem 0 0 0.625rem',
      backgroundColor: '#538fff',
    },
  },
  alertItemHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  alertItemTitle: {
    color: '#231f20',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    lineHeight: '1.5',
    fontWeight: 700,
  },
  alertItemMeta: {
    flexShrink: 0,
    color: '#8b8b8b',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.5',
    textAlign: 'right',
    fontWeight: 400,
  },
  alertItemDescription: {
    color: '#333',
    fontFamily: 'NotoSans-Medium',
    lineHeight: '1.5',
    fontSize: '0.875rem',
  },
  arrow: {
    color: '#FFFFFF',
    '&::before': {
      boxShadow: '0 0 0.5rem 0 rgba(125, 134, 169, 0.2)',
    },
  },
  tooltip: {
    borderRadius: '0.625rem',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#FFFFFF',
    padding: '1rem',
    color: '#333',
    width: '40rem',
    maxWidth: 'calc(100vw - 2rem)',
    boxSizing: 'border-box',
  },
}));

const formatAlertDate = (date, language) => {
  if (!date) {
    return '';
  }

  const dateValue = moment(date);
  if (!dateValue.isValid()) {
    return date;
  }

  const locale = language === LANGUAGE_ZH_HK ? 'zh-HK' : 'en-GB';
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(
    dateValue.toDate()
  );
};

export const CmsAlertItem = ({ alertItem }) => {
  const classes = useStyles();
  const language = useSelector((state) => state.settings.language);
  const effectiveFrom = formatAlertDate(alertItem?.effectiveFrom, language);
  const effectiveTo = formatAlertDate(alertItem?.effectiveTo, language);
  const effectiveDateRange = [effectiveFrom, effectiveTo].filter(Boolean).join(' - ');

  return (
    <div className={classes.alertItem}>
      <div className={classes.alertItemHeader}>
        <div className={classes.alertItemTitle}>{alertItem?.alertDesc}</div>
        <div className={classes.alertItemMeta}>{`${effectiveDateRange} by ${alertItem?.createHospCd}`}</div>
      </div>
      {alertItem?.remark && <div className={classes.alertItemDescription}>{alertItem?.remark}</div>}
    </div>
  );
};

const CmsAlertList = ({ alertList }) => {
  const classes = useStyles();

  return (
    <Box className={classes.alertList}>
      {alertList.map((item, index) => (
        <CmsAlertItem key={item?.patientKey ?? index} alertItem={item} />
      ))}
    </Box>
  );
};

const CmsAlert = ({ variant = CMS_ALERT_ICON_VARIANT.STANDARD, alertsData = [], containerClass }) => {
  const classes = useStyles();
  const isWide = variant === CMS_ALERT_ICON_VARIANT.WIDE;
  const [toolTipOpen, setToolTipOpen] = useState(false);
  const icon = isWide ? (
    <CmsAlertWideIcon className={classes.wideIcon} />
  ) : (
    <CmsAlertStandardIcon className={classes.standardIcon} />
  );
  const handleOpen = (event) => {
    event.stopPropagation();
    setToolTipOpen(true);
  };

  const handleTooltipClose = (event) => {
    event.stopPropagation();
    setToolTipOpen(false);
  };

  if (!isNotEmptyArray(alertsData)) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box className={containerClass}>
        <Tooltip
          id="cms-alert-tooltip"
          classes={classes}
          title={<CmsAlertList alertList={alertsData} />}
          arrow
          TransitionComponent={Zoom}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          onClose={handleTooltipClose}
          open={toolTipOpen}
        >
          <ButtonBase
            className={classes.trigger}
            onClick={handleOpen}
            onKeyUp={(event) => event.stopPropagation()}
            aria-label="CMS Alert"
          >
            {icon}
          </ButtonBase>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

export default CmsAlert;
